// Doc section 18 "Event batching": collect in memory, flush every few
// seconds, flush after N events, flush on pagehide, use sendBeacon.

export interface BatchOptions {
  endpoint: string;
  flushIntervalMs?: number;
  maxBatchSize?: number;
  maxQueueSize?: number;
  buildPayload: (events: unknown[]) => unknown;
}

export class EventBatcher {
  private queue: unknown[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly opts: Required<BatchOptions>;

  constructor(opts: BatchOptions) {
    this.opts = {
      flushIntervalMs: 5000,
      maxBatchSize: 20,
      maxQueueSize: 200, // hard cap — matches server's CollectPayloadSchema events.max(200)
      ...opts,
    };
    this.start();
  }

  private start(): void {
    this.timer = setInterval(() => this.flush(), this.opts.flushIntervalMs);

    const flushOnHide = () => this.flush(true);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushOnHide();
    });
    window.addEventListener("pagehide", flushOnHide);
  }

  push(event: unknown): void {
    if (this.queue.length >= this.opts.maxQueueSize) {
      // Drop oldest rather than grow unbounded — analytics must never
      // become a memory-leak risk on a long-lived SPA-like session.
      this.queue.shift();
    }
    this.queue.push(event);
    if (this.queue.length >= this.opts.maxBatchSize) {
      this.flush();
    }
  }

  flush(useBeacon = false): void {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0, this.opts.maxBatchSize);
    const payload = this.opts.buildPayload(batch);
    const body = JSON.stringify(payload);

    try {
      // JSON beacons use credentials:include across origins. Our collector
      // deliberately disallows credentialed CORS, so queued beacons can fail
      // after sendBeacon returns true. Use keepalive fetch with omit instead.
      const sameOrigin = new URL(this.opts.endpoint, location.href).origin === location.origin;
      if (useBeacon && sameOrigin && "sendBeacon" in navigator) {
        const blob = new Blob([body], { type: "application/json" });
        const ok = navigator.sendBeacon(this.opts.endpoint, blob);
        if (ok) return;
        // sendBeacon can fail synchronously (queue full) — fall through to fetch.
      }
      void fetch(this.opts.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        credentials: "omit",
      }).catch(() => {
        // Never let a network failure surface anywhere near the shopper
        // (doc section 1 "fail silently if analytics is unavailable").
      });
    } catch {
      /* analytics must never throw into the page */
    }
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
  }

  clear(): void { this.queue.length = 0; }
}
